import { useState } from 'react';
import { Phone, Mail, MapPin, MessageSquare } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const Contact = () => {
  const [openFAQ, setOpenFAQ] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [isConsented, setIsConsented] = useState(false);

  const faqs = [
    {
      question: "What services do you offer?",
      answer: "ProjectProse offers two complementary services: professional property management for landlords with guaranteed rent schemes and tenant management, plus a premium portfolio of vacation rentals and serviced accommodation for guests seeking flexible stays."
    },
    {
      question: "What is the guaranteed rent scheme?",
      answer: "Our Guaranteed Rent scheme ensures you receive a fixed monthly payment, whether your property is tenanted or vacant. This provides financial stability and removes the stress of rental voids. We handle all tenant sourcing, management, and maintenance while you receive consistent income."
    },
    {
      question: "How do you find and vet tenants for my property?",
      answer: "We market your property, vet potential tenants rigorously, and manage them throughout the tenancy to ensure a smooth experience. Our process includes thorough background checks, employment verification, and personal interviews to ensure quality occupancy and minimize risk."
    },
    {
      question: "What is your vacation rental service?",
      answer: "We manage premium short-term vacation rentals and serviced accommodation for guests. Our properties are fully furnished, professionally managed, and available for flexible stays. We handle all bookings, guest communication, cleaning, and maintenance."
    },
    {
      question: "Can I book one of your vacation rental properties?",
      answer: "Yes! Browse our portfolio of quality vacation rental properties on our website. Simply select your dates, choose your preferred property, and book through our secure booking system. We provide transparent pricing and dedicated guest support throughout your stay."
    },
    {
      question: "What areas do you cover?",
      answer: "We operate across England, UK, providing nationwide coverage for both property management services for landlords and vacation rental properties. Contact us to discuss your specific location and how we can assist with your property investment or accommodation needs."
    },
    {
      question: "Who handles maintenance and repairs?",
      answer: "From routine upkeep to emergency repairs, we ensure properties are always in excellent condition. We work with a network of trusted, vetted contractors and provide 24/7 support as needed to maintain property value and guest satisfaction."
    },
    {
      question: "What does your compliance service include?",
      answer: "For property management clients, we take care of all landlord legal requirements, including safety certificates, tenancy agreements, and compliance with local regulations. For vacation rentals, we ensure all properties meet safety standards and guest protection requirements."
    },
    {
      question: "How can I get started?",
      answer: "Whether you're a landlord seeking property management or a guest looking to book accommodation, getting started is simple! Reach out via phone, email, or our contact form. Landlords: we'll discuss your portfolio and provide customized solutions. Guests: browse properties and book directly on our website."
    }
  ];

  return (
    <div className="pb-24 px-6 bg-white">
      <Helmet>
        <title>Contact ProjectProse | Property Management &amp; Rental Enquiries</title>
        <meta
          name="description"
          content="Get in touch with ProjectProse for landlord property management or vacation rental bookings across England. Call, email or use our contact form for a free consultation."
        />
        <link rel="canonical" href="https://www.project-prose.com/contact" />
      </Helmet>
      <div className="pt-32 pb-16">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12">
            <h2 className="text-sm uppercase tracking-[0.3em] text-prose-brand font-bold mb-4 border-b-4 w-fit border-prose-gold">Contact Us</h2>
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-display text-prose-black" data-aos="fade-up">
              Get In <span className="italic font-light text-gray-400">Touch.</span>
            </h1>
          </div>
          <p className="text-gray-600 text-sm sm:text-base md:text-lg max-w-2xl" data-aos="fade-up" data-aos-delay="100">
            Have questions about our services? Our team is here to help. Reach out today for a free consultation.
          </p>
        </div>
      </div>

      <div className="px-6 mb-20">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 lg:grid-cols-5 gap-6" data-aos="fade-up">
          {[
            { icon: <Phone size={24} />, label: "Telephone", value: "+44 1470275001", desc: "Available 9am-6pm" },
            { icon: <Phone size={24} />, label: "Mobile", value: "+44 7478036678", desc: "Available 9am-6pm" },
            { icon: <Mail size={24} />, label: "Email", value: "info@project-prose.com", desc: "We'll respond within 24h" },
            { icon: <MessageSquare size={24} />, label: "Live Chat", value: "Coming Soon", desc: "" },
            { icon: <MapPin size={24} />, label: "Location", value: "England, UK", desc: "Nationwide coverage" }
          ].map((contact, i) => (
            <div key={i} className={`p-6 rounded-xl text-center transition-all duration-300 border hover:scale-105 hover:-translate-y-2 ${
              i % 2 === 1
                ? 'border-prose-gold/30 hover:border-prose-gold hover:bg-prose-gold-light/10 hover:shadow-[0_0_20px_rgba(212,175,55,0.2)]'
                : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50 hover:shadow-lg'
            }`}>
              <div className={`flex justify-center mb-4 group-hover:scale-125 group-hover:-translate-y-2 transition-all duration-300 ${i % 2 === 1 ? 'text-prose-gold' : 'text-prose-brand'}`}>{contact.icon}</div>
              <h3 className="font-bold text-lg md:text-xl mb-1 text-prose-black">{contact.label}</h3>
              <p className="text-prose-black font-semibold mb-1 text-xs sm:text-sm">{contact.value}</p>
              <p className="text-xs sm:text-sm text-gray-500">{contact.desc}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto grid lg:grid-cols-3 gap-12 mb-20" data-aos="fade-up">
        <div className="lg:col-span-2">
          <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-display font-bold mb-8">Send us a Message</h2>
          {submitSuccess && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-800 font-semibold text-sm sm:text-base">✓ Message sent successfully! We'll get back to you soon.</p>
            </div>
          )}
          <form action="https://formsubmit.co/info@project-prose.com" method="POST" onSubmit={() => {
            setIsSubmitting(true);
            setSubmitSuccess(true);
            setTimeout(() => setSubmitSuccess(false), 5000);
          }} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-xs sm:text-sm font-semibold mb-2 text-prose-black">Full Name</label>
                <input 
                  type="text"
                  name="fullName"
                  placeholder="John Smith"
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-prose-brand transition-colors text-sm"
                />
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-semibold mb-2 text-prose-black">Email Address</label>
                <input 
                  type="email"
                  name="email"
                  placeholder="john@example.com"
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-prose-brand transition-colors"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-xs sm:text-sm font-semibold mb-2 text-prose-black">Phone Number</label>
                <input 
                  type="tel"
                  name="phone"
                  placeholder="07700 123456"
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-prose-brand transition-colors text-sm"
                />
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-semibold mb-2 text-prose-black">Service Interest</label>
                <input 
                  type="text"
                  name="serviceInterest"
                  placeholder="e.g. Property Management, Maintenance..."
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-prose-brand transition-colors text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-semibold mb-2 text-prose-black">Message</label>
              <textarea 
                name="message"
                rows="6"
                placeholder="Tell us about your property and what you're looking for..."
                required
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-prose-brand resize-none transition-colors text-sm"
              ></textarea>
            </div>

            <div className="flex items-center gap-3">
              <input 
                type="checkbox" 
                id="consent" 
                name="consent"
                checked={isConsented}
                onChange={(e) => setIsConsented(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300" 
              />
              <label htmlFor="consent" className="text-xs sm:text-sm text-gray-600">
                I consent to ProjectProse contacting me about my inquiry
              </label>
            </div>

            <button type="submit" disabled={isSubmitting || !isConsented} className="w-full bg-prose-brand text-white py-4 rounded-lg font-semibold hover:bg-prose-brand/90 hover:scale-105 hover:shadow-lg transition-all duration-300 text-sm sm:text-base disabled:opacity-70 disabled:cursor-not-allowed">
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        </div>

        <div className="bg-prose-brand text-white p-8 rounded-2xl h-fit">
          <h3 className="text-lg sm:text-2xl md:text-3xl font-display font-bold mb-8">Response Times</h3>
          <div className="space-y-6">
            <div>
              <p className="font-semibold mb-2 text-sm sm:text-base">Phone Enquiries</p>
              <p className="text-xs sm:text-sm text-prose-gold-light">Answered within 2 hours during business hours</p>
            </div>
            <div>
              <p className="font-semibold mb-2 text-sm sm:text-base">Email Enquiries</p>
              <p className="text-xs sm:text-sm text-prose-gold-light">Responded within 24 hours</p>
            </div>
            <div>
              <p className="font-semibold mb-2 text-sm sm:text-base">Callback Requests</p>
              <p className="text-xs sm:text-sm text-prose-gold-light">Within 4 hours, at your preferred time</p>
            </div>
            <hr className="my-6 border-prose-brand/30" />
            <div className="bg-prose-brand/20 p-4 rounded-lg border border-prose-gold/30">
              <p className="text-xs text-prose-gold-light mb-3 uppercase tracking-widest font-semibold">Office Hours</p>
              <div className="space-y-1 text-xs sm:text-sm font-medium">
                <p>Mon-Fri: 9:00am - 6:00pm</p>
                <p>Sat: 10:00am - 2:00pm</p>
                <p>Sun: Closed</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto mb-20 border-t border-gray-100 pt-20" data-aos="fade-up">
        <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-display font-bold mb-12 text-center">Frequently Asked Questions</h2>
        <div className="space-y-4">
          {faqs.map((faq, i) => (
            <div key={i} className="border border-gray-200 rounded-xl overflow-hidden">
              <button
                onClick={() => setOpenFAQ(openFAQ === i ? -1 : i)}
                className="w-full px-6 py-5 flex justify-between items-center hover:bg-gray-50 transition-colors text-left"
              >
                <h3 className="font-semibold text-prose-black text-sm sm:text-base">{faq.question}</h3>
                <span className={`text-prose-brand text-2xl transform transition-transform ${openFAQ === i ? 'rotate-45' : ''}`}>
                  +
                </span>
              </button>
              {openFAQ === i && (
                <div className="px-6 py-5 bg-gray-50 border-t border-gray-200">
                  <p className="text-gray-700 text-xs sm:text-sm leading-relaxed text-justify">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

    </div>
  );
};

export default Contact;